// src/pages/admin/StudentBulkImport.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API from '../../api';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import { SIGN_IN_LINK } from '../../config/accountAccessCopy';
import { normalizeImportedPhone } from '../../utils/phone';
import { isValidPersonName } from '../../utils/name';
import {
    getDateFormatPlaceholder,
    toISODateFromInput,
} from '../../utils/date';
import { apiErrorMessage } from '../../utils/apiError';

// ─── Template Definition ─────────────────────────────────────────────────────

const TEMPLATE_HEADERS = [
    'firstName', 'lastName', 'middleName', 'gender', 'dob',
    'username', 'email', 'phoneNumber',
    'addressLine1', 'addressLine2', 'city', 'state', 'country', 'pincode',
];

const TEMPLATE_SAMPLE = [
    'Rahul', 'Sharma', '', 'MALE', '2005-03-15',
    'rahul.sharma', 'rahul.sharma@example.com', '9876543210',
    '101 MG Road', 'Near City Mall', 'Mumbai', 'Maharashtra', 'India', '400001',
];

const REQUIRED_FIELDS = ['firstName', 'lastName', 'gender', 'username', 'email', 'phoneNumber'];

function downloadTemplate() {
    const lines = [
        TEMPLATE_HEADERS.join(','),
        TEMPLATE_SAMPLE.join(','),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// ─── CSV Parser ──────────────────────────────────────────────────────────────

function parseCSVLine(line) {
    const fields = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
            if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
            else { inQuotes = !inQuotes; }
        } else if (ch === ',' && !inQuotes) {
            fields.push(current.trim());
            current = '';
        } else {
            current += ch;
        }
    }
    fields.push(current.trim());
    return fields;
}

// Maps various column name spellings → canonical field name
const HEADER_ALIASES = {
    firstname: 'firstName',
    first_name: 'firstName',
    lastname: 'lastName',
    last_name: 'lastName',
    surname: 'lastName',
    middlename: 'middleName',
    middle_name: 'middleName',
    gender: 'gender',
    sex: 'gender',
    dob: 'dob',
    dateofbirth: 'dob',
    date_of_birth: 'dob',
    birthdate: 'dob',
    username: 'username',
    user_name: 'username',
    email: 'email',
    emailaddress: 'email',
    email_address: 'email',
    phonenumber: 'phoneNumber',
    phone_number: 'phoneNumber',
    phone: 'phoneNumber',
    mobile: 'phoneNumber',
    mobilenumber: 'phoneNumber',
    addressline1: 'addressLine1',
    address_line1: 'addressLine1',
    address: 'addressLine1',
    addressline2: 'addressLine2',
    address_line2: 'addressLine2',
    city: 'city',
    state: 'state',
    country: 'country',
    pincode: 'pincode',
    pin: 'pincode',
    zip: 'pincode',
    zipcode: 'pincode',
    password: 'password',
};

function parseCSV(text) {
    // Strip UTF-8 BOM if present; normalize line endings
    const normalized = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const allLines = normalized.split('\n');
    const lines = allLines.filter(l => l.trim() !== '');

    if (lines.length === 0) return { mappedHeaders: [], rows: [] };

    // Header row
    const rawHeaders = parseCSVLine(lines[0]);
    const mappedHeaders = rawHeaders.map(h => {
        const key = h.toLowerCase().replace(/[\s_-]+/g, '').replace(/[^a-z0-9]/g, '');
        return HEADER_ALIASES[key] || h; // fall back to original if unknown
    });

    // Data rows
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        if (!line.trim()) continue;
        const fields = parseCSVLine(line);
        const row = {};
        mappedHeaders.forEach((h, idx) => {
            row[h] = fields[idx] !== undefined ? fields[idx].trim() : '';
        });
        row._rowNum = i + 1; // 1-based row number relative to original file (header = row 1)
        rows.push(row);
    }
    return { mappedHeaders, rows };
}

// ─── Row Validator ───────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
const VALID_GENDERS = ['MALE', 'FEMALE', 'OTHER'];

function normalizeImportDob(raw) {
    if (!raw) return '';
    const trimmed = String(raw).trim();
    if (DATE_ISO_RE.test(trimmed)) return trimmed;
    return toISODateFromInput(trimmed) || '';
}

function validateRow(row, t) {
    const errors = [];
    const req = (field) => t('pages.studentBulkImport.validation.required', { field, defaultValue: '{{field}} is required' });
    const invalidName = (field) => t('pages.studentBulkImport.validation.invalidName', { field, defaultValue: '{{field}} must be a valid name (letters, spaces, hyphens, apostrophes; accents allowed)' });

    if (!row.firstName) errors.push(req('firstName'));
    else if (!isValidPersonName(row.firstName)) errors.push(invalidName('firstName'));

    if (!row.lastName) errors.push(req('lastName'));
    else if (!isValidPersonName(row.lastName)) errors.push(invalidName('lastName'));

    if (row.middleName && !isValidPersonName(row.middleName)) errors.push(invalidName('middleName'));

    if (!row.gender) errors.push(t('pages.studentBulkImport.validation.genderRequired', { defaultValue: 'gender is required (MALE / FEMALE / OTHER)' }));
    else if (!VALID_GENDERS.includes(row.gender.toUpperCase())) {
        errors.push(t('pages.studentBulkImport.validation.genderInvalid', { value: row.gender, defaultValue: 'gender "{{value}}" is invalid — must be MALE, FEMALE, or OTHER' }));
    }

    if (row.dob) {
        const iso = normalizeImportDob(row.dob);
        if (!iso) {
            errors.push(t('pages.studentBulkImport.validation.dobFormat', { format: getDateFormatPlaceholder(), defaultValue: 'dob must be {{format}} or YYYY-MM-DD' }));
        } else {
            const d = new Date(`${iso}T12:00:00`);
            if (isNaN(d.getTime())) errors.push(t('pages.studentBulkImport.validation.dobInvalid', { defaultValue: 'dob is not a valid date' }));
        }
    }

    if (!row.username) errors.push(req('username'));

    if (!row.email) errors.push(req('email'));
    else if (!EMAIL_RE.test(row.email)) {
        errors.push(t('pages.studentBulkImport.validation.emailInvalid', { value: row.email, defaultValue: 'email "{{value}}" is not valid' }));
    }

    if (!row.phoneNumber) errors.push(req('phoneNumber'));
    else {
        const phoneCheck = normalizeImportedPhone(row.phoneNumber);
        if (!phoneCheck.valid) {
            errors.push(phoneCheck.message || t('pages.studentBulkImport.validation.phoneInvalid', { defaultValue: 'phoneNumber is not valid' }));
        }
    }

    return errors;
}

// ─── Duplicate detector based on API error message ───────────────────────────

function isDuplicateError(msg) {
    return /already exists|duplicate|username.*taken|email.*taken|unique|constraint/i.test(msg);
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function StudentBulkImport() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // ── License gate ──────────────────────────────────────────────────────────
    const [licenseChecking, setLicenseChecking] = useState(true);
    const [enterpriseLocked, setEnterpriseLocked] = useState(false);

    useEffect(() => {
        axios.get('/api/public/license/info')
            .then(res => {
                const edition = (res.data?.edition || '').toUpperCase();
                setEnterpriseLocked(edition !== 'ENTERPRISE');
            })
            .catch(() => {
                // If we can't check, don't block access — fail open
                setEnterpriseLocked(false);
            })
            .finally(() => setLicenseChecking(false));
    }, []);

    // Settings
    const [sendInvite, setSendInvite] = useState(true);
    const [defaultPassword, setDefaultPassword] = useState('Welcome@123');

    // File / CSV state
    const [parsedData, setParsedData] = useState(null);
    const [fileName, setFileName] = useState('');
    const fileInputRef = useRef(null);
    const [dragging, setDragging] = useState(false);

    // Import runtime state
    const [importing, setImporting] = useState(false);
    const [progressPct, setProgressPct] = useState(0);
    const [currentMsg, setCurrentMsg] = useState('');

    // Results
    const [results, setResults] = useState(null);
    const [importDone, setImportDone] = useState(false);

    // ── File Handling ────────────────────────────────────────────────────────

    const handleFile = (file) => {
        if (!file) return;
        if (!file.name.match(/\.csv$/i)) {
            alert(t('pages.studentBulkImport.csvOnlyAlert'));
            return;
        }
        setFileName(file.name);
        setImportDone(false);
        setResults(null);
        setProgressPct(0);
        setCurrentMsg('');

        const reader = new FileReader();
        reader.onload = (e) => {
            const parsed = parseCSV(e.target.result);
            if (parsed.rows.length === 0) {
                alert(t('pages.studentBulkImport.noDataRowsAlert'));
                setParsedData(null);
                setFileName('');
            } else {
                setParsedData(parsed);
            }
        };
        reader.readAsText(file, 'UTF-8');
    };

    const handleFileInput = (e) => handleFile(e.target.files[0]);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        handleFile(e.dataTransfer.files[0]);
    };

    const resetAll = () => {
        setParsedData(null);
        setFileName('');
        setResults(null);
        setImportDone(false);
        setProgressPct(0);
        setCurrentMsg('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // ── Import Logic ─────────────────────────────────────────────────────────

    const handleStartImport = async () => {
        if (!parsedData || parsedData.rows.length === 0) return;

        if (!sendInvite && (!defaultPassword || defaultPassword.length < 6)) {
            alert(t('pages.studentBulkImport.passwordPlaceholder'));
            return;
        }

        setImporting(true);
        setImportDone(false);
        setResults(null);
        setProgressPct(0);

        const rows = parsedData.rows;
        const total = rows.length;
        const resultList = [];

        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const displayName = [row.firstName, row.lastName].filter(Boolean).join(' ') || t('common.rowN', { n: row._rowNum });
            setCurrentMsg(t('pages.studentBulkImport.processingRow', {
                current: i + 1,
                total,
                name: displayName,
            }));

            // ── Client-side validation ────────────────────────────────────
            const validationErrors = validateRow(row, t);
            if (validationErrors.length > 0) {
                resultList.push({
                    rowNum: row._rowNum,
                    name: displayName,
                    username: row.username || '—',
                    email: row.email || '—',
                    status: 'failed',
                    reason: validationErrors.join('; '),
                });
                setProgressPct(Math.round(((i + 1) / total) * 100));
                continue;
            }

            // ── Build API payload ─────────────────────────────────────────
            const payload = {
                firstName: row.firstName.trim(),
                lastName: row.lastName.trim(),
                gender: row.gender.trim().toUpperCase(),
                username: row.username.trim(),
                email: row.email.trim(),
                phoneNumber: normalizeImportedPhone(row.phoneNumber).normalized,
                active: true,
                roles: ['STUDENT'],
                sendInvite,
            };

            if (row.middleName) payload.middleName = row.middleName.trim();
            if (row.dob) payload.dob = normalizeImportDob(row.dob);
            if (row.addressLine1) payload.addressLine1 = row.addressLine1.trim();
            if (row.addressLine2) payload.addressLine2 = row.addressLine2.trim();
            if (row.city) payload.city = row.city.trim();
            if (row.state) payload.state = row.state.trim();
            if (row.country) payload.country = row.country.trim();
            if (row.pincode) payload.pincode = row.pincode.trim();

            if (!sendInvite) {
                payload.password = row.password?.trim() || defaultPassword;
            }

            // ── API call ──────────────────────────────────────────────────
            try {
                await API.post('/admin/users', payload);
                resultList.push({
                    rowNum: row._rowNum,
                    name: displayName,
                    username: row.username,
                    email: row.email,
                    status: 'success',
                    reason: '',
                });
            } catch (err) {
                const raw = err?.response?.data?.message
                    || (typeof err?.response?.data === 'string' ? err.response.data : null)
                    || err.message;
                const errStr = typeof raw === 'string' ? raw : '';
                const duplicate = isDuplicateError(errStr);
                resultList.push({
                    rowNum: row._rowNum,
                    name: displayName,
                    username: row.username || '—',
                    email: row.email || '—',
                    status: duplicate ? 'skipped' : 'failed',
                    reason: duplicate
                        ? t('pages.studentBulkImport.duplicateSkipped')
                        : apiErrorMessage(err, t('pages.studentBulkImport.unknownError')),
                });
            }

            setProgressPct(Math.round(((i + 1) / total) * 100));
        }

        setResults(resultList);
        setImporting(false);
        setImportDone(true);
        setCurrentMsg('');
    };

    // ── Derived counts ────────────────────────────────────────────────────────

    const successCount = results ? results.filter(r => r.status === 'success').length : 0;
    const skippedCount = results ? results.filter(r => r.status === 'skipped').length : 0;
    const failedCount = results ? results.filter(r => r.status === 'failed').length : 0;

    // Preview columns — show the most important ones that exist in the parsed data
    const PREVIEW_COLS = ['firstName', 'lastName', 'gender', 'username', 'email', 'phoneNumber', 'dob'];
    const previewCols = parsedData
        ? PREVIEW_COLS.filter(c => parsedData.mappedHeaders.includes(c))
        : [];

    // ─── Render ───────────────────────────────────────────────────────────────

    if (licenseChecking) {
        return (
            <div className="page-shell-narrow">
                <div className="flex items-center justify-center py-24">
                    <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
                </div>
            </div>
        );
    }

    if (enterpriseLocked) {
        return (
            <div className="page-shell-narrow">
                <PageHeaderSection
                    title={t('pages.studentBulkImport.title')}
                    subtitle={t('pages.studentBulkImport.enterpriseSubtitle')}
                />

                <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 shadow-sm">
                    <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <h2 className="text-lg font-semibold text-gray-900">{t('pages.studentBulkImport.enterpriseFeature')}</h2>
                            <p className="mt-2 text-sm leading-6 text-gray-700">
                                {t('pages.studentBulkImport.enterpriseBody')}
                            </p>
                            <div className="mt-4 rounded-xl border border-amber-200 bg-white px-4 py-3 text-sm text-gray-700">
                                <Trans
                                    i18nKey="pages.studentBulkImport.enterpriseHint"
                                    values={{ addStudentLink: t('pages.studentBulkImport.addStudentLink') }}
                                    components={{
                                        1: (
                                            <button
                                                type="button"
                                                className="text-primary-600 underline font-medium"
                                                onClick={() => navigate('/admin/students/new')}
                                            />
                                        ),
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-shell-narrow">
            <PageHeaderSection
                title={t('pages.studentBulkImport.title')}
                subtitle={t('pages.studentBulkImport.subtitle')}
                aside={(
                    <button type="button" onClick={downloadTemplate} className="btn-page-action-accent flex-shrink-0">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {t('pages.studentBulkImport.downloadTemplate')}
                    </button>
                )}
            />

            {/* ── Import Report (shown after done) ──────────────────────────── */}
            {importDone && results && (
                <div className="space-y-6">
                    {/* Summary cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="data-card p-5 text-center">
                            <p className="text-4xl font-black text-slate-800">{results.length}</p>
                            <p className="text-sm text-slate-500 mt-1 font-medium">{t('pages.studentBulkImport.totalRows')}</p>
                        </div>
                        <div className="data-card p-5 text-center">
                            <p className="text-4xl font-black text-green-600">{successCount}</p>
                            <p className="text-sm text-slate-500 mt-1 font-medium">{t('pages.studentBulkImport.imported')}</p>
                        </div>
                        <div className="data-card p-5 text-center">
                            <p className="text-4xl font-black text-yellow-500">{skippedCount}</p>
                            <p className="text-sm text-slate-500 mt-1 font-medium">{t('pages.studentBulkImport.skipped')}</p>
                        </div>
                        <div className="data-card p-5 text-center">
                            <p className="text-4xl font-black text-red-600">{failedCount}</p>
                            <p className="text-sm text-slate-500 mt-1 font-medium">{t('pages.studentBulkImport.failed')}</p>
                        </div>
                    </div>

                    {/* Outcome banner */}
                    {failedCount === 0 && skippedCount === 0 ? (
                        <div className="rounded-2xl bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 p-4 flex items-center gap-3">
                            <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-green-800 font-medium">{t('pages.studentBulkImport.allSuccess', { count: successCount })}</p>
                        </div>
                    ) : (
                        <div className="rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 p-4 flex items-start gap-3">
                            <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div>
                                <p className="text-amber-800 font-medium">{t('pages.studentBulkImport.completedWithIssues')}</p>
                                <p className="text-amber-700 text-sm mt-0.5">
                                    {t('pages.studentBulkImport.summaryIssues', { success: successCount, skipped: skippedCount, failed: failedCount })}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Detail table */}
                    <div className="data-card overflow-hidden">
                        <div className="flex items-center justify-between p-5 border-b border-slate-100">
                            <h3 className="text-lg font-bold text-slate-800">{t('pages.studentBulkImport.detailedReport')}</h3>
                            <button onClick={resetAll} className="btn-secondary text-sm px-4 py-2">
                                {t('pages.studentBulkImport.importAnother')}
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="table-header">
                                    <tr>
                                        <th className="table-header-cell w-12">{t('pages.studentBulkImport.colNum')}</th>
                                        <th className="table-header-cell">{t('pages.studentBulkImport.colName')}</th>
                                        <th className="table-header-cell">{t('pages.studentBulkImport.colUsername')}</th>
                                        <th className="table-header-cell">{t('pages.studentBulkImport.colEmail')}</th>
                                        <th className="table-header-cell">{t('pages.studentBulkImport.colStatus')}</th>
                                        <th className="table-header-cell">{t('pages.studentBulkImport.colReason')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {results.map((r, idx) => (
                                        <tr key={idx} className="table-row">
                                            <td className="table-cell text-slate-400 font-mono text-xs">{r.rowNum}</td>
                                            <td className="table-cell font-medium text-slate-800">{r.name}</td>
                                            <td className="table-cell font-mono text-xs text-slate-600">{r.username}</td>
                                            <td className="table-cell text-xs text-slate-600">{r.email}</td>
                                            <td className="table-cell">
                                                {r.status === 'success' && <span className="badge-success">{t('pages.studentBulkImport.statusImported')}</span>}
                                                {r.status === 'skipped' && <span className="badge-warning">{t('pages.studentBulkImport.statusSkipped')}</span>}
                                                {r.status === 'failed' && <span className="badge-danger">{t('pages.studentBulkImport.statusFailed')}</span>}
                                            </td>
                                            <td className="table-cell text-xs text-slate-500 max-w-xs break-words">{r.reason || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 flex-wrap">
                        <button onClick={() => navigate('/admin/students')} className="btn-page-action-primary">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {t('pages.studentBulkImport.viewAllStudents')}
                        </button>
                    </div>
                </div>
            )}

            {/* ── Upload + Config + Preview (hidden while results are shown) ── */}
            {!importDone && (
                <div className="space-y-6">

                    {/* ── Step 1: Settings ─────────────────────────────────── */}
                    <div className="data-card p-6">
                        <h3 className="text-base font-bold text-slate-700 mb-4 flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">1</span>
                            {t('pages.studentBulkImport.step1')}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <p className="text-sm font-semibold text-slate-700 mb-3">{t('pages.studentBulkImport.passwordStrategy')}</p>
                                <div className="space-y-2">
                                    <label className="flex items-center gap-3 cursor-pointer group">
                                        <input
                                            type="radio"
                                            name="passwordStrategy"
                                            checked={sendInvite}
                                            onChange={() => setSendInvite(true)}
                                            className="text-primary-600 w-4 h-4"
                                        />
                                        <span className="text-sm text-slate-700 group-hover:text-slate-900">
                                            {SIGN_IN_LINK.bulkImportOption}
                                        </span>
                                    </label>
                                    <label className="flex items-center gap-3 cursor-pointer group">
                                        <input
                                            type="radio"
                                            name="passwordStrategy"
                                            checked={!sendInvite}
                                            onChange={() => setSendInvite(false)}
                                            className="text-primary-600 w-4 h-4"
                                        />
                                        <span className="text-sm text-slate-700 group-hover:text-slate-900">
                                            {t('pages.studentBulkImport.defaultPasswordOption')}
                                        </span>
                                    </label>
                                </div>
                            </div>

                            {!sendInvite && (
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                                        {t('pages.studentBulkImport.defaultPassword')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={defaultPassword}
                                        onChange={e => setDefaultPassword(e.target.value)}
                                        className="input"
                                        placeholder={t('pages.studentBulkImport.passwordPlaceholder')}
                                    />
                                    <p className="mt-1.5 text-xs text-slate-500">
                                        {t('pages.studentBulkImport.passwordHint')}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Step 2: File Upload ──────────────────────────────── */}
                    <div className="data-card p-6">
                        <h3 className="text-base font-bold text-slate-700 mb-4 flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">2</span>
                            {t('pages.studentBulkImport.step2')}
                        </h3>

                        <div
                            className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all duration-200 ${dragging
                                ? 'border-primary-500 bg-primary-50 scale-[1.01]'
                                : 'border-slate-300 hover:border-primary-400 hover:bg-slate-50'
                                }`}
                            onDragOver={e => { e.preventDefault(); setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv"
                                onChange={handleFileInput}
                                className="hidden"
                            />

                            {fileName ? (
                                <div>
                                    <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                                        <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <p className="text-sm font-semibold text-slate-800">📄 {fileName}</p>
                                    <p className="text-sm text-green-700 mt-1 font-medium">
                                        {t('pages.studentBulkImport.rowsFound', { count: parsedData?.rows.length || 0 })}
                                    </p>
                                    <p className="text-xs text-slate-400 mt-2">{t('pages.studentBulkImport.replaceFile')}</p>
                                </div>
                            ) : (
                                <div>
                                    <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                                        <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                    </div>
                                    <p className="text-sm font-semibold text-slate-700">{t('pages.studentBulkImport.dropHint')}</p>
                                    <p className="text-xs text-slate-400 mt-1.5">{t('pages.studentBulkImport.csvOnly')}</p>
                                    <p className="text-xs text-slate-400 mt-0.5">
                                        {t('pages.studentBulkImport.noTemplate')}{' '}
                                        <span
                                            className="text-primary-600 underline"
                                            onClick={e => { e.stopPropagation(); downloadTemplate(); }}
                                        >
                                            {t('pages.studentBulkImport.downloadHere')}
                                        </span>
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Step 3: Preview + Start ──────────────────────────── */}
                    {parsedData && parsedData.rows.length > 0 && !importing && (
                        <div className="data-card p-6">
                            <h3 className="text-base font-bold text-slate-700 mb-4 flex items-center gap-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">3</span>
                                {t('pages.studentBulkImport.step3')}
                            </h3>

                            {/* Preview table */}
                            <div className="overflow-x-auto rounded-xl border border-slate-200 mb-4">
                                <table className="w-full text-sm">
                                    <thead className="table-header">
                                        <tr>
                                            {previewCols.map(c => (
                                                <th key={c} className="table-header-cell">
                                                    {c}
                                                    {REQUIRED_FIELDS.includes(c) && <span className="text-red-400 ml-1">*</span>}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {parsedData.rows.slice(0, 5).map((row, idx) => (
                                            <tr key={idx} className="table-row">
                                                {previewCols.map(c => (
                                                    <td key={c} className="table-cell">
                                                        {row[c] || <span className="text-slate-300">—</span>}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {parsedData.rows.length > 5 && (
                                <p className="text-xs text-slate-400 mb-4 text-center">
                                    {t('pages.studentBulkImport.showingFirst', { total: parsedData.rows.length })}
                                </p>
                            )}

                            <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-slate-100">
                                <p className="text-sm text-slate-600">
                                    {t('pages.studentBulkImport.rowsFound', { count: parsedData.rows.length })}
                                </p>
                                <button
                                    onClick={handleStartImport}
                                    className="btn-page-action-primary"
                                >
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l4-4m0 0l4 4m-4-4v12" />
                                    </svg>
                                    {t('pages.studentBulkImport.startImport')}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── Progress Bar ─────────────────────────────────────── */}
                    {importing && (
                        <div className="data-card p-6">
                            <h3 className="text-base font-bold text-slate-700 mb-5 flex items-center gap-2">
                                <span className="inline-flex w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                                {t('pages.studentBulkImport.importing')}
                            </h3>
                            <p className="text-sm text-slate-600 mb-3">{currentMsg}</p>
                            <div className="w-full bg-slate-200 rounded-full h-4 overflow-hidden">
                                <div
                                    className="bg-gradient-to-r from-primary-500 to-indigo-500 h-4 rounded-full transition-all duration-300 flex items-center justify-end pr-2"
                                    style={{ width: `${progressPct}%` }}
                                >
                                    {progressPct >= 10 && (
                                        <span className="text-white text-xs font-bold">{progressPct}%</span>
                                    )}
                                </div>
                            </div>
                            {progressPct < 10 && (
                                <p className="text-xs text-slate-500 mt-1">{progressPct}%</p>
                            )}
                            <p className="text-xs text-slate-400 mt-3">
                                ⚠️ Please do not close or refresh this page while the import is running.
                            </p>
                        </div>
                    )}

                    {/* ── Format Guide ─────────────────────────────────────── */}
                    {!fileName && (
                        <div className="data-card p-6">
                            <h3 className="text-base font-bold text-slate-700 mb-4">📋 {t('pages.studentBulkImport.csvGuide', { defaultValue: 'CSV Format Guide' })}</h3>
                            <div className="overflow-x-auto rounded-xl border border-slate-200 mb-4">
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            <th className="table-header-cell">{t('common.name')}</th>
                                            <th className="table-header-cell">{t('common.status')}</th>
                                            <th className="table-header-cell">{t('common.description')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {[
                                            ['firstName', true, t('pages.studentBulkImport.cols.firstName', { defaultValue: 'Unicode letters allowed (e.g. François, Müller)' })],
                                            ['lastName', true, t('pages.studentBulkImport.cols.lastName', { defaultValue: 'Unicode letters allowed (e.g. Dupont, van der Berg)' })],
                                            ['middleName', false, t('pages.studentBulkImport.cols.middleName', { defaultValue: 'Optional — accents, spaces, hyphens, apostrophes OK' })],
                                            ['gender', true, t('pages.studentBulkImport.cols.gender', { defaultValue: 'MALE | FEMALE | OTHER (uppercase)' })],
                                            ['dob', false, t('pages.studentBulkImport.cols.dob', { format: getDateFormatPlaceholder(), defaultValue: 'Date of birth in academy regional format ({{format}}) or YYYY-MM-DD' })],
                                            ['username', true, t('pages.studentBulkImport.cols.username', { defaultValue: 'Unique login username (e.g. rahul.sharma)' })],
                                            ['email', true, t('pages.studentBulkImport.cols.email', { defaultValue: 'Valid email address — must be unique' })],
                                            ['phoneNumber', true, t('pages.studentBulkImport.cols.phoneNumber', { defaultValue: 'Local number or full international (+…). Local numbers use academy country from Regional Settings.' })],
                                            ['addressLine1', false, t('pages.studentBulkImport.cols.addressLine1', { defaultValue: 'Street address, flat / house number' })],
                                            ['addressLine2', false, t('pages.studentBulkImport.cols.addressLine2', { defaultValue: 'Additional address info — landmark, area, etc.' })],
                                            ['city', false, t('pages.studentBulkImport.cols.city', { defaultValue: 'City' })],
                                            ['state', false, t('pages.studentBulkImport.cols.state', { defaultValue: 'State / province' })],
                                            ['country', false, t('pages.studentBulkImport.cols.country', { defaultValue: 'Country' })],
                                            ['pincode', false, t('pages.studentBulkImport.cols.pincode', { defaultValue: 'PIN / ZIP code' })],
                                            ['password', false, t('pages.studentBulkImport.cols.password', { defaultValue: 'Optional — overrides default password for this row' })],
                                        ].map(([col, req, note]) => (
                                            <tr key={col} className="table-row">
                                                <td className="table-cell font-mono font-semibold text-slate-700">{col}</td>
                                                <td className="table-cell">
                                                    {req
                                                        ? <span className="badge-danger">{t('common.required', { defaultValue: 'Required' })}</span>
                                                        : <span className="badge-gray">{t('common.optional', { defaultValue: 'Optional' })}</span>
                                                    }
                                                </td>
                                                <td className="table-cell text-slate-500">{note}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
